import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { get } from '../api/client';
import { useAuth } from './AuthContext';

/**
 * Most operational screens are "an event, then a module". Rather than each page
 * fetching the event list and managing its own selection, the choice lives here
 * and persists across navigation.
 */
const EventContext = createContext(null);

/**
 * Picks the event an operator most likely wants on arrival: one that is live
 * now, else the soonest upcoming one, else the most recently finished.
 * (The list arrives sorted by start date descending.)
 */
function pickDefault(rows) {
  if (!rows.length) return '';
  const now = Date.now();

  const live = rows.find((e) => e.status === 'live');
  if (live) return live._id;

  const upcoming = rows
    .filter((e) => new Date(e.endDate).getTime() >= now && e.status !== 'cancelled')
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  if (upcoming.length) return upcoming[0]._id;

  return rows[0]._id;
}

export function EventProvider({ children }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(() => localStorage.getItem('gl_event') || '');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const rows = await get('/events', { limit: 100 });
      setEvents(rows);
      setEventId((current) => {
        if (current && rows.some((e) => e._id === current)) return current;
        return pickDefault(rows);
      });
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (eventId) localStorage.setItem('gl_event', eventId);
  }, [eventId]);

  const value = useMemo(() => ({
    events,
    eventId,
    setEventId,
    event: events.find((e) => e._id === eventId) || null,
    /**
     * True when the selected event has that module switched on. Unknown or
     * still-loading events read as on, so nothing flickers away mid-load.
     */
    hasModule: (key) => {
      const ev = events.find((e) => e._id === eventId);
      return !ev?.modules || ev.modules[key] !== false;
    },
    loading,
    refresh,
  }), [events, eventId, loading, refresh]);

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useEvents() {
  const ctx = useContext(EventContext);
  if (!ctx) throw new Error('useEvents must be used inside <EventProvider>');
  return ctx;
}
